import { lazy } from 'react';
import { useReportsRoutes } from './Routes/useReportsRoutes';
import { useKFlags } from '@/hooks/use-k-flags';
import { useSelector } from 'react-redux';
import {
  selectCurrUser,
  selectCurrUserPermission,
} from '@/store/bootstrap/selectors';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';

const Rippling = lazy(
  () =>
    import(
      /* webpackChunkName: "Rippling" */ '@/pages/rippling/RipplingIntegration'
    )
);

const Circles = lazy(
  () => import(/* webpackChunkName: "CirclesList" */ '@/pages/circles/list')
);
const CircleDetails = lazy(
  () =>
    import(/* webpackChunkName: "CircleDetails" */ '@/pages/circles/details')
);

const ContactsDetails = lazy(
  () =>
    import(/* webpackChunkName: "ContactsDetails" */ '@/pages/contacts/details')
);
const ContactsList = lazy(
  () => import(/* webpackChunkName: "ContactsList" */ '@/pages/contacts/list')
);
const ContactsListV2 = lazy(
  () =>
    import(
      /* webpackChunkName: "ContactsList" */ '@/pages/contacts/list-revamp'
    )
);
const Settings = lazy(
  () => import(/* webpackChunkName: "Settings" */ '@/pages/settings')
);

const MCODashboard = lazy(
  () => import(/* webpackChunkName: "Dashboard" */ '@/pages/mco-dashboard')
);

const ATSDashboard = lazy(
  () => import(/* webpackChunkName: "Dashboard" */ '@/pages/dashboard-revamp')
);

const AgentForgeDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: "AgentForgeDashboard" */ '@/pages/agentforge/index'
    )
);

const PendingActivities = lazy(
  () =>
    import(
      /* webpackChunkName: "PendingActivities" */ '@/pages/dashboard-revamp/pending-activities'
    )
);

const MCOList = lazy(
  () => import(/* webpackChunkName: "MCOList" */ '@/pages/mco/list')
);

const FlowDetails = lazy(
  () => import(/* webpackChunkName: "MCODetails"*/ '@/pages/mco/details')
);

const MCOBlueprint = lazy(
  () => import(/* webpackChunkName: "MCOBlueprint" */ '@/pages/mco/blueprint')
);

const Tasks = lazy(
  () => import(/* webpackChunkName: "Tasks" */ '@/pages/tasks')
);

const ATSTasks = lazy(
  () => import(/* webpackChunkName: "Tasks" */ '@/pages/tasks-revamp')
);

const TemplateList = lazy(
  () => import(/* webpackChunkName: "TemplateList" */ '@/pages/templates/list')
);

const TemplateCreate = lazy(
  () => import(/* webpackChunkName: "TemplateCreate" */ '@/pages/templates/new')
);

const TemplateEdit = lazy(
  () => import(/* webpackChunkName: "TemplateEdit" */ '@/pages/templates/edit')
);

const TemplateClone = lazy(
  () =>
    import(/* webpackChunkName: "TemplateClone" */ '@/pages/templates/clone')
);

const ProjectsList = lazy(
  () => import(/* webpackChunkName: "ProjectsList"*/ '@/pages/projects/list')
);

const ProjectDetails = lazy(
  () =>
    import(/* webpackChunkName: "ProjectDetails"*/ '@/pages/projects/details')
);

const ATSComponents = lazy(
  () => import(/* webpackChunkName: "ATSComponents"*/ '@/pages/ats-components/')
);

const AtsTable = lazy(
  () => import(/* webpackChunkName: "AtsTable"*/ '@/pages/ats-table')
);

const SchedulerTest = lazy(
  () =>
    import(
      /* webpackChunkName: "SchedulerTest"*/ '@/pages/KInterviewScheduler/SchedulerTest'
    )
);

const AtsJobCreation = lazy(
  () =>
    import(/* webpackChunkName: "AtsJobCreation"*/ '@/pages/ats-job-creation')
);

const AtsJob = lazy(
  () => import(/* webpackChunkName: "AtsJob"*/ '@/pages/ats-jobs')
);

const CareerPortal = lazy(
  () => import(/* webpackChunkName: "CareerPortal"*/ '@/pages/career-portal')
);

const CareerPortalBuilder = lazy(
  () =>
    import(
      /* webpackChunkName: "CareerPortalBuilder"*/ '@/pages/career-portal/builder'
    )
);

// const InterviewList = lazy(() =>
//   import(
//     /* webpackChunkName: "InterviewList"*/ '@/pages/interview/interview-list'
//   )
// );

const InterviewRevampList = lazy(
  () =>
    import(
      /* webpackChunkName: "InterviewRevampList"*/ '@/pages/interviews/list'
    )
);

const InterviewRevampDetailsV2 = lazy(
  () =>
    import(
      /* webpackChunkName: "InterviewRevampDetailsV2"*/ '@/pages/interviews-revamp/details'
    )
);

const MarkInterviewStatus = lazy(
  () =>
    import(
      /* webpackChunkName: "MarkInterviewStatus"*/ '@/pages/interviews-revamp/mark-interview-status'
    )
);

const Referrals = lazy(
  () => import(/* webpackChunkName: "ReferralsList"*/ '@/pages/referrals')
);

const InternalJobs = lazy(
  () => import(/* webpackChunkName: "InternalJobs"*/ '@/pages/internalJobs')
);

const InternalJobDetail = lazy(
  () =>
    import(
      /* webpackChunkName: "InternalJobDetail"*/ '@/pages/internalJobs/detail'
    )
);

// const InterviewDetails = lazy(() =>
//   import(
//     /* webpackChunkName: "InterviewDetails"*/ '@/pages/interview/interview-details'
//   )
// );

const CSVExports = lazy(
  () => import(/* webpackChunkName: "CSVExports"*/ '@/pages/csv-exports')
);

const ConversationalAnalytics = lazy(
  () =>
    import(
      /* webpackChunkName: "ConversationalAnalytics"*/ '@/pages/conversational-analytics'
    )
);

const RecentActivities = lazy(() =>
  import(
    /* webpackChunkName: "RecentActivities"*/ '@/pages/notifications/recent-activities'
  ).then((module) => ({
    default: module.RecentActivities,
  }))
);

const JobsWithTabs = lazy(() =>
  import(
    /* webpackChunkName: "JobsWithTabs"*/ '@/pages/ats-jobs/JobsWithTabs'
  ).then((module) => ({
    default: module.JobsWithTabs,
  }))
);

const RequisitionsWithTabs = lazy(() =>
  import(
    /* webpackChunkName: "RequisitionsWithTabs"*/ '@/components/shared'
  ).then((module) => ({
    default: module.RequisitionsWithTabs,
  }))
);

export const useGetRoutes = () => {
  const {
    feAtsComponents = false,
    ats_enabled,
    feAgentForgeV1 = false,
    internalJobsPost = false,
  } = useKFlags();
  const currentUser = useSelector(selectCurrUser) || {};

  // Get reports routes using the new hook
  const permissions = useSelector(selectCurrUserPermission) || {};
  const { routes: reportRoutes } = useReportsRoutes();
  const {
    requisitionsPermissions,
    // AgentForge access uses the job-creation permission.
    isJobCreateAllowed: canAccessAgentForge,
  } = useAtsPermissions();

  const isCommTemplatesEnabledForAts =
    permissions?.['api_ats_v1_ats_comm_template.create?'];

  const isRequisitionsEnabled =
    currentUser?.requisition?.enabled && requisitionsPermissions.index;

  const atsRoutes = ats_enabled
    ? [
        {
          route: '/jobs/:jobId',
          component: AtsJobCreation,
          isAllowedToExternalUsers: true,
        },
        {
          route: '/jobs',
          component: isRequisitionsEnabled ? JobsWithTabs : AtsJob, // Fallback to original component
          title: 'Jobs | Kula AI',
        },
        isRequisitionsEnabled && {
          route: '/requisitions',
          component: RequisitionsWithTabs,
          title: 'Requisitions | Kula AI',
        },
      ]
    : [];

  const circleRoutes = [
    {
      route: '/circles/:companyId',
      component: CircleDetails,
      title: 'Circle Details | Kula AI',
    },
    { route: '/circles', component: Circles, title: 'Circles | Kula AI' },
  ];

  const integrationsRoutes = [
    {
      route: '/rippling',
      component: Rippling,
      title: 'Rippling | Kula AI',
    },
  ];

  const contactRoutes = [
    {
      route: '/candidates/:contactId',
      component: ContactsDetails,
      skipTitleUpdate: true,
    },
    {
      route: '/candidates',
      component: ats_enabled ? ContactsListV2 : ContactsList,
      title: 'Candidates | Kula AI',
    },
  ];

  const settingRoutes = [
    {
      route: '/settings',
      component: Settings,
      skipTitleUpdate: true,
      // since doing a custom title update in the settings nav
    },
  ];

  const taskRoutes = [
    {
      route: '/tasks',
      component: ats_enabled ? ATSTasks : Tasks,
      title: 'Tasks | Kula AI',
    },
  ];

  const mcoRoutes = [
    {
      route: '/blueprints/:blueprintId',
      component: MCOBlueprint,
      title: 'Blueprint Details | Kula AI',
    },
    {
      route: '/flows/:outreachId',
      component: FlowDetails,
      skipTitleUpdate: true,
    },
    { route: '/flows', component: MCOList, title: 'Flows | Kula AI' },
  ];

  const templateRoutes = [
    { route: '/templates/:templateId/edit', component: TemplateEdit },
    { route: '/templates/:templateId/clone', component: TemplateClone },
    {
      route: '/templates/new',
      component: TemplateCreate,
      title: 'New Template | Kula AI',
    },
    {
      route: '/templates',
      component: TemplateList,
      title: 'Templates | Kula AI',
    },
  ];

  const dashboardRoutes = [
    {
      route: '/dashboard/pending-activities',
      component: ats_enabled ? PendingActivities : () => null,
      title: 'Pending Activities | Kula AI',
    },
    {
      route: '/dashboard',
      component: ats_enabled ? ATSDashboard : MCODashboard,
      title: 'Dashboard | Kula AI',
    },
    ...(feAgentForgeV1 && canAccessAgentForge
      ? [
          // Thread deep link — must be registered before `/ask-kula` so Switch
          // does not treat `/ask-kula/c/...` as a prefix of `/ask-kula`.
          {
            route: '/ask-kula/c/:sessionId',
            component: AgentForgeDashboard,
            title: 'Ask Kula | Kula AI',
          },
          {
            route: '/ask-kula',
            component: AgentForgeDashboard,
            title: 'Ask Kula | Kula AI',
            exact: true,
          },
        ]
      : []),
  ];

  const notificationRoutes = [
    {
      route: '/notifications/recent-activities',
      component: RecentActivities,
      title: 'Recent Activities | Kula AI',
    },
  ];

  const conversationalAnalyticsRoutes = [
    {
      route: '/conversational-analytics/:chatId',
      component: ConversationalAnalytics,
      title: 'Conversational Analytics | Kula AI',
    },
  ];

  const careerPortalRoutes = ats_enabled
    ? [
        {
          route: '/career-portal/themes/:themeId',
          component: CareerPortalBuilder,
          title: 'Career Portal Builder | Kula AI',
        },
        {
          route: '/career-portal',
          component: CareerPortal,
          title: 'Career Portal | Kula AI',
        },
      ]
    : [];

  const projectRoutes = [
    {
      route: '/projects/reviews',
      component: ProjectsList,
      isAllowedToExternalUsers: true,
      title: 'Reviews | Kula AI',
    },
    {
      route: '/projects/:projectId',
      component: ProjectDetails,
      isAllowedToExternalUsers: true,
      skipTitleUpdate: true,
    },
    {
      route: '/projects',
      component: ProjectsList,
      isAllowedToExternalUsers: true,
      title: 'Projects | Kula AI',
    },
  ];

  const reviewRoutes = [];

  const interviewRevampRoutes = ats_enabled
    ? [
        // todo later remove interviews/all route
        {
          route: '/interviews/all',
          component: InterviewRevampList,
          title: 'All Interviews | Kula AI',
        },
        {
          route: '/interviews/past',
          component: InterviewRevampList,
          title: 'Past Interviews | Kula AI',
        },
        {
          route: '/interviews/upcoming',
          component: InterviewRevampList,
          title: 'Upcoming Interviews | Kula AI',
        },
        {
          route: '/interviews/:interviewId/mark-interview-status',
          component: MarkInterviewStatus,
          title: 'Mark Interview Status | Kula AI',
        },
        {
          // The optional `:assistantMode` segment (`/interviews/:id/prep|live|coach`) is the RESTful
          // deep link that opens the interviewer assistant in that mode. It's one route (not a
          // separate one) so stripping the segment back to the base path after opening doesn't remount
          // the page. The pattern constrains the value, so typos/other sub-paths fall through.
          route: '/interviews/:interviewId/:assistantMode(prep|live|coach)?',
          component: InterviewRevampDetailsV2,
          title: 'Interviews | Kula AI',
        },
        {
          route: '/interviews',
          component: InterviewRevampList,
          title: 'Interviews | Kula AI',
        },
      ].filter(Boolean)
    : [];

  const csvExportRoutes = [
    {
      route: '/csv-exports/:exportId',
      component: CSVExports,
      title: 'CSV Exports | Kula AI',
    },
  ];

  const feDevTesting = feAtsComponents
    ? [
        {
          route: '/design-system',
          component: ATSComponents,
          title: 'ATS Components | Kula AI',
        },
        {
          route: '/ats_table',
          component: AtsTable,
          title: 'ATS Table | Kula AI',
        },
        { route: '/testing', component: SchedulerTest },
      ]
    : [];

  const referralsRoutes = ats_enabled
    ? [
        {
          route: '/referrals',
          component: Referrals,
          isAllowedToExternalUsers: true,
          title: 'Referrals | Kula AI',
        },
      ]
    : [];

  const internalJobsRoutes = internalJobsPost
    ? [
        {
          route: '/internal-job-board',
          component: InternalJobs,
          exact: true,
          isAllowedToExternalUsers: true,
          title: 'Internal job board | Kula AI',
        },
        {
          route: '/internal-job-board/:jobId',
          component: InternalJobDetail,
          exact: true,
          isAllowedToExternalUsers: true,
          title: 'Internal job board | Kula AI',
        },
      ]
    : [];

  return [
    ...atsRoutes,
    ...circleRoutes,
    ...contactRoutes,
    ...settingRoutes,
    ...taskRoutes,
    ...mcoRoutes,
    ...(ats_enabled // Check if ats_enabled is true
      ? isCommTemplatesEnabledForAts // If ats_enabled is true, check if isCommTemplatesEnabledForAts is true
        ? templateRoutes // If true, include templateRoutes
        : [] // If false, include an empty array (no routes)
      : templateRoutes), // If ats_enabled is false, revert to original (all)
    ...dashboardRoutes,
    ...notificationRoutes,
    ...reportRoutes,
    ...projectRoutes,
    ...reviewRoutes,
    // ...interviewRoutes,
    ...interviewRevampRoutes,
    ...feDevTesting,
    ...careerPortalRoutes,
    ...csvExportRoutes,
    ...referralsRoutes,
    ...internalJobsRoutes,
    ...conversationalAnalyticsRoutes,
    ...integrationsRoutes,
  ].filter(Boolean);
};
